/**
 * Flight storage composable — persists flights using Capacitor Filesystem.
 *
 * Storage layout (inside Directory.Data):
 *   flights/
 *     index.json          — array of FlightMeta (no points)
 *     tracks/<id>.csv     — GPS track for each flight
 *
 * CSV format per track file:
 *   timestamp,latitude,longitude,altitude
 *   1706000000000,-33.96490000,18.60170000,0.0
 *
 * This works transparently on:
 *   • Android / iOS  →  native filesystem
 *   • Desktop browser →  IndexedDB (Capacitor web fallback)
 */

import { ref } from 'vue';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import type { Flight, FlightMeta, FlightPoint, LogbookFields } from '@/types/flight';
import type { RemoteFlight } from '@/types/api';
import { isAuthenticated } from '@/services/api';
import { uploadFlight, fetchRemoteFlights, downloadTrack, updateRemoteFlightName, updateRemoteFlightLogbook, enableFlightSharing, deleteRemoteFlight } from '@/services/sync';
import { pointsToCsv, csvToPoints } from '@/lib/trackCsv';

// ── Paths ───────────────────────────────────────────────────────────────
const INDEX_PATH = 'flights/index.json';
const TRACKS_DIR = 'flights/tracks';
const PHOTOS_DIR = 'flights/photos';

// ── Reactive state (shared singleton) ───────────────────────────────────
const flights = ref<FlightMeta[]>([]);
const isLoading = ref(false);

// ── Internal I/O ────────────────────────────────────────────────────────

async function readIndex(): Promise<FlightMeta[]> {
  try {
    const result = await Filesystem.readFile({
      path: INDEX_PATH,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
    return JSON.parse(result.data as string) as FlightMeta[];
  } catch {
    // File doesn't exist yet — that's fine
    return [];
  }
}

async function writeIndex(meta: FlightMeta[]): Promise<void> {
  await Filesystem.writeFile({
    path: INDEX_PATH,
    data: JSON.stringify(meta, null, 2),
    directory: Directory.Data,
    encoding: Encoding.UTF8,
    recursive: true,
  });
}

async function writeTrack(id: string, csv: string): Promise<void> {
  await Filesystem.writeFile({
    path: `${TRACKS_DIR}/${id}.csv`,
    data: csv,
    directory: Directory.Data,
    encoding: Encoding.UTF8,
    recursive: true,
  });
}

async function readTrack(id: string): Promise<string> {
  const result = await Filesystem.readFile({
    path: `${TRACKS_DIR}/${id}.csv`,
    directory: Directory.Data,
    encoding: Encoding.UTF8,
  });
  return result.data as string;
}

async function deleteTrackFile(id: string): Promise<void> {
  try {
    await Filesystem.deleteFile({
      path: `${TRACKS_DIR}/${id}.csv`,
      directory: Directory.Data,
    });
  } catch {
    // File might not exist; ignore
  }
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64 ?? '');
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Clear in-memory flights and all persisted flight data (index, tracks, and photos). Used on logout so the next user does not see the previous user's flights. */
async function clearAllFlights(): Promise<void> {
  try {
    await Filesystem.rmdir({
      path: 'flights',
      directory: Directory.Data,
      recursive: true,
    });
  } catch {
    // Directory may not exist yet
  }
  flights.value = [];
}

// ── Migration from legacy localStorage ──────────────────────────────────

const LEGACY_KEY = 'flight-recordings';

interface LegacyFlight {
  id: string;
  name: string;
  startTime: number;
  endTime: number;
  points: FlightPoint[];
  distance: number;
  maxAltitude: number;
  duration: number;
}

async function migrateLegacyStorage(): Promise<void> {
  const raw = localStorage.getItem(LEGACY_KEY);
  if (!raw) return;

  try {
    const legacyFlights: LegacyFlight[] = JSON.parse(raw);
    if (!Array.isArray(legacyFlights) || legacyFlights.length === 0) return;

    const now = new Date().toISOString();
    const metas: FlightMeta[] = [];

    for (const lf of legacyFlights) {
      // Write CSV track
      const csv = pointsToCsv(lf.points);
      await writeTrack(lf.id, csv);

      // Build metadata
      metas.push({
        id: lf.id,
        name: lf.name,
        startTime: lf.startTime,
        endTime: lf.endTime,
        distance: lf.distance,
        maxAltitude: lf.maxAltitude,
        duration: lf.duration,
        syncStatus: 'local',
        createdAt: new Date(lf.startTime).toISOString(),
        updatedAt: now,
      });
    }

    await writeIndex(metas);

    // Clear legacy data after successful migration
    localStorage.removeItem(LEGACY_KEY);
    console.info(`[storage] Migrated ${metas.length} flights from localStorage`);
  } catch (err) {
    console.error('[storage] Legacy migration failed:', err);
  }
}

// ── Public API ──────────────────────────────────────────────────────────

export function useFlightStorage() {
  /** Load the flight index from disk, then fetch and merge remote flights if authenticated */
  async function loadFlights(): Promise<void> {
    isLoading.value = true;
    try {
      // Run one-time migration if legacy data exists
      await migrateLegacyStorage();

      // Load local flights
      let metas = await readIndex();

      // If authenticated, fetch remote flights and merge
      if (isAuthenticated()) {
        metas = await mergeRemoteFlights(metas);
      }

      metas.sort((a, b) => b.startTime - a.startTime);

      // Deduplicate flights that share the same remoteId (server-side duplicates)
      const seenRemoteIds = new Set<string>();
      metas = metas.filter((m) => {
        if (!m.remoteId) return true;
        if (seenRemoteIds.has(m.remoteId)) return false;
        seenRemoteIds.add(m.remoteId);
        return true;
      });

      flights.value = metas;
    } catch (err) {
      console.error('[storage] Failed to load flights:', err);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Fetch remote flights and merge with local index.
   * Remote-only flights are added locally with syncStatus='synced'.
   */
  async function mergeRemoteFlights(localMetas: FlightMeta[]): Promise<FlightMeta[]> {
    try {
      const result = await fetchRemoteFlights();
      if (!result.success || !result.data) {
        console.warn('[storage] Failed to fetch remote flights:', result.error?.message);
        return localMetas;
      }

      const remoteFlights = result.data;
      console.info(`[storage] Fetched ${remoteFlights.length} remote flights`);

      const remoteById = new Map(remoteFlights.map((r) => [r.id, r]));

      // Update existing local flights with remote shareUrl and replayUrl so list shows replay ready
      const updatedMetas = localMetas.map((m) => {
        if (!m.remoteId) return m;
        const remote = remoteById.get(m.remoteId);
        const updates: Partial<FlightMeta> = {};
        if (remote?.shareUrl != null) updates.shareUrl = remote.shareUrl ?? undefined;
        if (remote?.replayUrl != null) updates.replayUrl = remote.replayUrl ?? undefined;
        if (Object.keys(updates).length === 0) return m;
        return { ...m, ...updates };
      });

      // Build set of remote IDs we already have locally
      const localRemoteIds = new Set(
        updatedMetas.filter((meta) => meta.remoteId).map((meta) => meta.remoteId!),
      );

      // Find remote flights that don't exist locally
      const newRemote = remoteFlights.filter((r) => !localRemoteIds.has(r.id));

      if (newRemote.length === 0) {
        await writeIndex(updatedMetas);
        return updatedMetas;
      }

      if (newRemote.length > 0) {
        console.info(`[storage] Adding ${newRemote.length} new remote flights to local index`);
      }

      const newMetas: FlightMeta[] = newRemote.map((r) => remoteToLocalMeta(r));
      const merged = [...updatedMetas, ...newMetas];
      await writeIndex(merged);

      return merged;
    } catch (err) {
      console.error('[storage] Error merging remote flights:', err);
      return localMetas;
    }
  }

  /** Convert a RemoteFlight to FlightMeta for local storage. */
  function remoteToLocalMeta(remote: RemoteFlight): FlightMeta {
    return {
      id: `remote-${remote.id}`,
      remoteId: remote.id,
      name: remote.name,
      startTime: remote.startTime,
      endTime: remote.endTime,
      distance: remote.distance,
      maxAltitude: remote.maxAltitude,
      duration: remote.duration,
      syncStatus: 'synced',
      hobbsTime: remote.hobbsTime ?? undefined,
      fuelLevel: remote.fuelLevel ?? undefined,
      notes: remote.notes ?? undefined,
      site: remote.site ?? undefined,
      aircraft: remote.aircraft ?? undefined,
      shareUrl: remote.shareUrl ?? undefined,
      replayUrl: remote.replayUrl ?? undefined,
      createdAt: remote.createdAt,
      updatedAt: remote.updatedAt,
    };
  }

  /** Save a full flight (metadata + CSV track), then auto-upload if authenticated */
  async function saveFlight(flight: Flight): Promise<void> {
    const now = new Date().toISOString();

    // Build metadata (strip points)
    const meta: FlightMeta = {
      id: flight.id,
      name: flight.name,
      startTime: flight.startTime,
      endTime: flight.endTime,
      distance: flight.distance,
      maxAltitude: flight.maxAltitude,
      duration: flight.duration,
      syncStatus: 'local',
      hobbsTime: flight.hobbsTime,
      fuelLevel: flight.fuelLevel,
      notes: flight.notes,
      site: flight.site,
      aircraft: flight.aircraft,
      createdAt: now,
      updatedAt: now,
    };

    // Write track CSV
    const csv = pointsToCsv(flight.points);
    await writeTrack(flight.id, csv);

    // Update index
    const metas = await readIndex();
    metas.unshift(meta);
    await writeIndex(metas);

    // Refresh reactive state
    flights.value = [...metas].sort((a, b) => b.startTime - a.startTime);

    // Auto-upload in the background if the user is authenticated
    if (isAuthenticated()) {
      autoUploadFlight(meta, flight.points).catch((err) => {
        console.warn('[storage] Background upload failed — will retry on next sync:', err);
      });
    }
  }

  /**
   * Upload a flight to the server in the background and update sync status.
   * This is fire-and-forget from the caller's perspective.
   */
  async function autoUploadFlight(meta: FlightMeta, points: FlightPoint[]): Promise<void> {
    try {
      const result = await uploadFlight(meta, points);

      if (result.success && result.data) {
        // Update the local index with sync status and remote ID
        await updateSyncStatus(meta.id, 'synced', result.data.id);
        console.info(`[storage] Flight ${meta.id} uploaded → remote ID ${result.data.id}`);
      } else {
        console.warn(`[storage] Upload failed for ${meta.id}:`, result.error?.message);
      }
    } catch (err) {
      console.warn(`[storage] Upload error for ${meta.id}:`, err);
    }
  }

  /** Update the syncStatus and optional remoteId for a flight in the index */
  async function updateSyncStatus(
    localId: string,
    syncStatus: FlightMeta['syncStatus'],
    remoteId?: string,
  ): Promise<void> {
    const metas = await readIndex();
    const meta = metas.find((m) => m.id === localId);
    if (!meta) return;

    meta.syncStatus = syncStatus;
    meta.updatedAt = new Date().toISOString();
    if (remoteId !== undefined) {
      meta.remoteId = remoteId;
    }

    await writeIndex(metas);
    flights.value = [...metas].sort((a, b) => b.startTime - a.startTime);
  }

  /** Load a full flight including its GPS points (downloads from server if needed) */
  async function getFlight(id: string): Promise<Flight | undefined> {
    const metas = await readIndex();
    const meta = metas.find((m) => m.id === id);
    if (!meta) return undefined;

    // Try to read from local storage first
    try {
      const csv = await readTrack(id);
      const points = csvToPoints(csv);
      return { ...meta, points };
    } catch {
      // Track not available locally
    }

    // If we have a remoteId, try to download from server
    if (meta.remoteId && isAuthenticated()) {
      try {
        console.info(`[storage] Downloading track for flight ${id} from server...`);
        const trackUrl = `/api/flights/${meta.remoteId}/track`;
        const points = await downloadTrack(trackUrl);

        if (points.length > 0) {
          // Cache locally for future use
          const csv = pointsToCsv(points);
          await writeTrack(id, csv);
          console.info(`[storage] Cached track for flight ${id} (${points.length} points)`);
          return { ...meta, points };
        }
      } catch (err) {
        console.error(`[storage] Failed to download track for ${id}:`, err);
      }
    }

    console.error(`[storage] No track available for ${id}`);
    return undefined;
  }

  /** Update a flight's name in the index (and on server if synced) */
  async function updateFlightName(id: string, name: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) return;

    const metas = await readIndex();
    const meta = metas.find((m) => m.id === id);
    if (!meta) return;

    meta.name = trimmed;
    meta.updatedAt = new Date().toISOString();

    await writeIndex(metas);
    flights.value = [...metas].sort((a, b) => b.startTime - a.startTime);

    // If synced, update on server
    if (meta.remoteId && isAuthenticated()) {
      try {
        const result = await updateRemoteFlightName(meta.remoteId, trimmed);
        if (!result.success) {
          meta.syncStatus = 'modified';
          meta.updatedAt = new Date().toISOString();
          await writeIndex(metas);
          flights.value = [...metas].sort((a, b) => b.startTime - a.startTime);
        }
      } catch {
        meta.syncStatus = 'modified';
        meta.updatedAt = new Date().toISOString();
        await writeIndex(metas);
        flights.value = [...metas].sort((a, b) => b.startTime - a.startTime);
      }
    }
  }

  /** Set share URL on a flight (after enabling sharing on the server). */
  async function setFlightShareUrl(id: string, shareUrl: string): Promise<void> {
    const metas = await readIndex();
    const meta = metas.find((m) => m.id === id);
    if (!meta) return;
    meta.shareUrl = shareUrl;
    meta.updatedAt = new Date().toISOString();
    await writeIndex(metas);
    flights.value = [...metas].sort((a, b) => b.startTime - a.startTime);
  }

  /** Set replay download URL on a flight after server generation completes. */
  async function setFlightReplayUrl(id: string, replayUrl: string): Promise<void> {
    const metas = await readIndex();
    const meta = metas.find((m) => m.id === id);
    if (!meta) return;
    meta.replayUrl = replayUrl;
    meta.updatedAt = new Date().toISOString();
    await writeIndex(metas);
    flights.value = [...metas].sort((a, b) => b.startTime - a.startTime);
  }

  /** Update a flight's logbook fields in the index (and on server if synced) */
  async function updateFlightLogbook(id: string, logbook: LogbookFields): Promise<void> {
    const metas = await readIndex();
    const meta = metas.find((m) => m.id === id);
    if (!meta) return;

    if (logbook.hobbsTime !== undefined) meta.hobbsTime = logbook.hobbsTime ?? undefined;
    if (logbook.fuelLevel !== undefined) meta.fuelLevel = logbook.fuelLevel ?? undefined;
    if (logbook.notes !== undefined) meta.notes = logbook.notes ?? undefined;
    if (logbook.site !== undefined) meta.site = logbook.site ?? undefined;
    if (logbook.aircraft !== undefined) meta.aircraft = logbook.aircraft ?? undefined;
    meta.updatedAt = new Date().toISOString();

    await writeIndex(metas);
    flights.value = [...metas].sort((a, b) => b.startTime - a.startTime);

    if (meta.remoteId && isAuthenticated()) {
      try {
        const result = await updateRemoteFlightLogbook(meta.remoteId, {
          hobbsTime: logbook.hobbsTime,
          fuelLevel: logbook.fuelLevel,
          notes: logbook.notes,
          site: logbook.site,
          aircraft: logbook.aircraft,
        });
        if (!result.success) {
          meta.syncStatus = 'modified';
          meta.updatedAt = new Date().toISOString();
          await writeIndex(metas);
          flights.value = [...metas].sort((a, b) => b.startTime - a.startTime);
        }
      } catch {
        meta.syncStatus = 'modified';
        meta.updatedAt = new Date().toISOString();
        await writeIndex(metas);
        flights.value = [...metas].sort((a, b) => b.startTime - a.startTime);
      }
    }
  }

  /** Delete a flight (metadata + track file). If synced, deletes on the server first. */
  async function deleteFlight(id: string): Promise<void> {
    const metas = await readIndex();
    const meta = metas.find((m) => m.id === id);
    if (meta?.remoteId && isAuthenticated()) {
      const result = await deleteRemoteFlight(meta.remoteId);
      if (!result.success) {
        console.warn('[storage] Failed to delete flight on server:', result.error?.message);
        throw new Error(result.error?.message ?? 'Failed to delete flight on server');
      }
    }
    const updated = metas.filter((m) => m.id !== id);
    await writeIndex(updated);
    await deleteTrackFile(id);
    flights.value = updated.sort((a, b) => b.startTime - a.startTime);
  }

  /** Get the raw CSV string for a flight (useful for export & sync) */
  async function getTrackCsv(id: string): Promise<string> {
    return readTrack(id);
  }

  /** List photo filenames for a flight (stored under flights/photos/<id>/) */
  async function getFlightPhotoPaths(flightId: string): Promise<string[]> {
    try {
      const result = await Filesystem.readdir({
        path: `${PHOTOS_DIR}/${flightId}`,
        directory: Directory.Data,
      });
      return (result.files || []).map((f) => (typeof f === 'string' ? f : f.name)).filter(Boolean);
    } catch {
      return [];
    }
  }

  /** Read a flight photo as a data URL for display */
  async function getFlightPhotoDataUrl(flightId: string, filename: string): Promise<string> {
    const result = await Filesystem.readFile({
      path: `${PHOTOS_DIR}/${flightId}/${filename}`,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
    const base64 = typeof result.data === 'string' ? result.data : '';
    const ext = filename.split('.').pop()?.toLowerCase() || 'jpg';
    const mime = ext === 'png' ? 'image/png' : ext === 'gif' ? 'image/gif' : 'image/jpeg';
    return `data:${mime};base64,${base64}`;
  }

  /** Save photo files for a flight (base64 stored in Data directory) */
  async function addFlightPhotos(flightId: string, files: File[]): Promise<void> {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const base64 = await fileToBase64(file);
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z]/g, '') || 'jpg';
      const filename = `${Date.now()}-${i}.${ext}`;
      await Filesystem.writeFile({
        path: `${PHOTOS_DIR}/${flightId}/${filename}`,
        data: base64,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
        recursive: true,
      });
    }
  }

  /** Remove one photo for a flight */
  async function deleteFlightPhoto(flightId: string, filename: string): Promise<void> {
    try {
      await Filesystem.deleteFile({
        path: `${PHOTOS_DIR}/${flightId}/${filename}`,
        directory: Directory.Data,
      });
    } catch {
      // ignore
    }
  }

  /** Export a flight as a downloadable CSV (browser) */
  async function exportFlightCsv(id: string): Promise<void> {
    const csv = await readTrack(id);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flight-${id}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Force refresh from server - fetches all remote flights and merges with local.
   * Call this when user explicitly wants to sync / refresh.
   */
  async function refreshFromServer(): Promise<void> {
    if (!isAuthenticated()) {
      console.warn('[storage] Cannot refresh from server - not authenticated');
      return;
    }

    isLoading.value = true;
    try {
      let metas = await readIndex();
      metas = await mergeRemoteFlights(metas);
      metas.sort((a, b) => b.startTime - a.startTime);
      flights.value = metas;
    } catch (err) {
      console.error('[storage] Failed to refresh from server:', err);
    } finally {
      isLoading.value = false;
    }
  }

  return {
    flights,
    isLoading,
    loadFlights,
    saveFlight,
    getFlight,
    updateFlightName,
    updateFlightLogbook,
    setFlightShareUrl,
    setFlightReplayUrl,
    deleteFlight,
    enableFlightSharing,
    getTrackCsv,
    exportFlightCsv,
    getFlightPhotoPaths,
    getFlightPhotoDataUrl,
    addFlightPhotos,
    deleteFlightPhoto,
    updateSyncStatus,
    refreshFromServer,
    clearAllFlights,
  };
}
